import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PencilIcon, TrashIcon, EyeIcon, ArrowDownTrayIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { IVideoGeneration, VideoGenerationStatus, VideoGenerationTypeLabels, IUserModel, getLabelsByLocale, Locale } from '@packages/global';
import StatusBadge from './StatusBadge';
import LazyVideo from '../common/LazyVideo';
import MediaPreviewModal from '../common/MediaPreviewModal';

interface VideoGenerationCardProps {
  item: IVideoGeneration;
  models: IUserModel[];
  onEdit: (item: IVideoGeneration) => void;
  onDelete: (id: string) => void;
}

export default function VideoGenerationCard({ item, models, onEdit, onDelete }: VideoGenerationCardProps) {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const videoGenerationTypeLabels = getLabelsByLocale(VideoGenerationTypeLabels, i18n.language as Locale);
  const [showPreview, setShowPreview] = useState(false);
  const [showReferenceImages, setShowReferenceImages] = useState(false);

  const getModelName = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.title : modelId;
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString(i18n.language);
  };

  const handleDownload = async () => {
    if (!item.resultVideoUrl) return;
    try {
      const response = await fetch(item.resultVideoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `video-generation-${item.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('下载失败:', error);
    }
  };

  const handleCardClick = () => {
    if (item.resultVideoUrl) setShowPreview(true);
  };

  return (
    <div
      className={`relative w-60 h-80 overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle hover:shadow-[0_0_20px_rgba(255,92,0,0.15)] hover:border-primary hover:-translate-y-1 transition-all duration-300 ${item.resultVideoUrl ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      <div className="absolute inset-0">
        {item.resultVideoUrl ? (
          <LazyVideo src={item.resultVideoUrl} className="w-full h-full object-cover" placeholderClassName="w-full h-full" autoPlayOnHover />
        ) : (
          <div className="w-full h-full bg-bg-elevated flex items-center justify-center">
            {item.status === VideoGenerationStatus.PENDING || item.status === VideoGenerationStatus.SUBMITTED || item.status === VideoGenerationStatus.PROCESSING ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-400 text-sm">
                  {item.status === VideoGenerationStatus.PENDING ? t('video.card.waitingProcess') : item.status === VideoGenerationStatus.SUBMITTED ? t('video.card.submitted') : t('video.card.generating')}
                </p>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <div className="w-16 h-16 bg-bg-form rounded-lg mx-auto mb-2 flex items-center justify-center">
                  <VideoCameraIcon className="w-8 h-8" />
                </div>
                <p className="text-sm">{t('video.card.noResult')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="absolute top-3 right-3"><StatusBadge status={item.status} /></div>

      <div className="absolute top-3 left-3 flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
        <div className="bg-black/50 text-white/90 px-2 py-1 rounded-md text-xs font-medium border border-white/20 backdrop-blur-sm">
          {videoGenerationTypeLabels[item.type]}
        </div>
        {item.referenceImages.length > 0 && (
          <button onClick={() => setShowReferenceImages(true)} className="bg-black/50 text-white/90 px-2 py-1 rounded-md text-xs font-medium border border-white/20 hover:bg-black/70 transition-colors backdrop-blur-sm cursor-pointer">
            {t('video.card.referenceCount', { count: item.referenceImages.length })}
          </button>
        )}
      </div>

      <div className="absolute inset-x-0 bottom-0 pt-20 pb-4 px-4">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(17,24,39,0.95) 0%, rgba(17,24,39,0.8) 40%, rgba(17,24,39,0.4) 70%, transparent 100%)' }}></div>
        <div className="relative z-10">
          <h4 className="font-semibold text-white truncate mb-2">{getModelName(item.modelId)}</h4>
          <div className="text-sm text-white/80 mb-2 line-clamp-2">{item.prompt}</div>
          <div className="text-xs text-white/60 mb-3">{formatDate(item.createdAt)}</div>
          {item.status === VideoGenerationStatus.FAILED && item.resultMessage && (
            <div className="text-xs text-red-400 mb-3 line-clamp-1" title={item.resultMessage}>{item.resultMessage}</div>
          )}
          <div className="flex justify-between items-center text-xs" onClick={(e) => e.stopPropagation()}>
            <span className="text-white/80">{item.aspectRatio} · {item.duration}s</span>
            <div className="flex gap-1">
              {item.resultVideoUrl && (
                <>
                  <button onClick={() => setShowPreview(true)} className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm" title={t('video.card.previewResult')}><EyeIcon className="h-4 w-4" /></button>
                  <button onClick={handleDownload} className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm" title={t('video.card.downloadResult')}><ArrowDownTrayIcon className="h-4 w-4" /></button>
                </>
              )}
              <button onClick={() => onEdit(item)} className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm" title={t('video.card.createFromThis')}><PencilIcon className="h-4 w-4" /></button>
              <button onClick={() => onDelete(item.id)} className="p-1.5 rounded-lg bg-black/40 hover:bg-red-500/80 text-white/80 hover:text-white transition-all backdrop-blur-sm"><TrashIcon className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </div>

      <MediaPreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} type="video" src={item.resultVideoUrl || ''} />

      {showReferenceImages && item.referenceImages.length > 0 && createPortal(
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[9999]" onClick={(e) => { e.stopPropagation(); setShowReferenceImages(false); }}>
          <div className="relative max-w-6xl max-h-full p-4" onClick={(e) => e.stopPropagation()}>
            <div className="bg-bg-surface rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-50 mb-4">{t('video.card.referenceImages')}</h3>
              <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {item.referenceImages.map((url, index) => (
                  <div key={index} className="relative">
                    <img src={url} alt={t('video.card.referenceImageAlt', { index: index + 1 })} className="w-full h-32 object-cover rounded border border-border-default" />
                    <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs">{index + 1}</div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={() => setShowReferenceImages(false)} className="absolute top-2 right-2 text-white bg-black/50 hover:bg-black/75 rounded-full p-2">✕</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
